import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTheme } from "@mui/material/styles";
import { ACTIONS, EVENTS, STATUS } from "react-joyride";
import type { Controls, EventData, Step, TooltipRenderProps } from "react-joyride";
import type { WalkthroughTourKey } from "../../api/activityFunctions";
import { TrackedJoyride } from "./TrackedJoyride";
import { getTourJoyride } from "./tourJoyrideStyles";
import { useTourSeen } from "./useTourSeen";
import TourFooter from "./TourFooter";
import { TourHintContext } from "./TourHintContext";

export interface SimpleStageTourHandle {
  replay(): void;
}

export interface SimpleStageTourProps {
  tourKey: WalkthroughTourKey;
  steps: Step[];
  /** Set false while the first tour target is loading or absent. */
  ready?: boolean;
}

/** Manual-only walkthrough for stages whose visible steps are supplied by the stage. */
const SimpleStageTour = forwardRef<SimpleStageTourHandle, SimpleStageTourProps>(
  ({ tourKey, steps, ready = true }, ref) => {
    const theme = useTheme();
    const { state, lastIndex, markPhaseAComplete, markSeen, reset } =
      useTourSeen(tourKey);
    const [run, setRun] = useState(false);
    const [showResumeHint, setShowResumeHint] = useState(false);
    const [startIndex, setStartIndex] = useState(0);
    const [replayNonce, setReplayNonce] = useState(0);
    const [pendingReplay, setPendingReplay] = useState(false);
    const currentIndexRef = useRef(0);
    const controlsRef = useRef<Controls | null>(null);
    const showResumeHintRef = useRef(showResumeHint);

    useEffect(() => {
      showResumeHintRef.current = showResumeHint;
    }, [showResumeHint]);

    const handleCallback = useCallback(
      (data: EventData, controls?: Controls) => {
        if (controls) controlsRef.current = controls;
        const { status, action, type, index } = data;
        if (type === EVENTS.STEP_BEFORE && typeof index === "number") {
          currentIndexRef.current = index;
        }
        if (
          status === STATUS.SKIPPED ||
          (action === ACTIONS.CLOSE && type === EVENTS.TOUR_END)
        ) {
          markPhaseAComplete(
            typeof index === "number" ? index : currentIndexRef.current
          );
          setShowResumeHint(true);
          return;
        }
        if (status === STATUS.FINISHED && type === EVENTS.TOUR_END) {
          markSeen();
          setRun(false);
        }
      },
      [markPhaseAComplete, markSeen]
    );

    useEffect(() => {
      if (!run || typeof window === "undefined") return;
      const handler = (event: KeyboardEvent) => {
        if (
          !["ArrowRight", "ArrowLeft", "ArrowUp", "ArrowDown", "Escape"].includes(
            event.key
          )
        ) {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        if (event.key === "Escape") {
          if (showResumeHintRef.current) {
            setShowResumeHint(false);
            setRun(false);
          } else {
            markPhaseAComplete(currentIndexRef.current);
            setShowResumeHint(true);
          }
          return;
        }
        if (event.key === "ArrowRight" || event.key === "ArrowDown") {
          controlsRef.current?.next?.();
        } else {
          controlsRef.current?.prev?.();
        }
      };
      window.addEventListener("keydown", handler);
      return () => window.removeEventListener("keydown", handler);
    }, [run, markPhaseAComplete]);

    const startReplay = useCallback(() => {
      setShowResumeHint(false);
      setReplayNonce((value) => value + 1);
      if (
        state === "phaseB-pending" &&
        lastIndex !== null &&
        lastIndex < steps.length
      ) {
        setStartIndex(lastIndex);
      } else {
        reset();
        setStartIndex(0);
      }
      setRun(true);
    }, [lastIndex, reset, state, steps.length]);

    useImperativeHandle(ref, () => ({
      replay: () => {
        if (!ready || steps.length === 0) {
          setPendingReplay(true);
          return;
        }
        setPendingReplay(false);
        startReplay();
      },
    }), [ready, startReplay, steps.length]);

    useEffect(() => {
      if (!pendingReplay || !ready || steps.length === 0) return;
      setPendingReplay(false);
      startReplay();
    }, [pendingReplay, ready, startReplay, steps.length]);

    const dismissResumeHint = useCallback(() => {
      setShowResumeHint(false);
      setRun(false);
    }, []);
    const requestExit = useCallback(() => {
      markPhaseAComplete(currentIndexRef.current);
      setShowResumeHint(true);
    }, [markPhaseAComplete]);
    const hintContextValue = useMemo(
      () => ({
        isHintMode: showResumeHint,
        onRequestExit: requestExit,
        onDismissHint: dismissResumeHint,
      }),
      [showResumeHint, requestExit, dismissResumeHint]
    );
    const joyride = useMemo(
      () => getTourJoyride(theme, { hideSpotlight: showResumeHint }),
      [theme, showResumeHint]
    );

    return (
      <TourHintContext.Provider value={hintContextValue}>
        <TrackedJoyride
          tourKey={tourKey}
          key={`joyride-${tourKey}-${startIndex}-${replayNonce}`}
          steps={steps}
          run={run && ready}
          initialStepIndex={startIndex}
          continuous
          locale={{ last: "Finish" }}
          tooltipComponent={TourFooter as unknown as React.ComponentType<TooltipRenderProps>}
          onEvent={handleCallback}
          options={joyride.options}
          styles={joyride.styles}
        />
      </TourHintContext.Provider>
    );
  }
);

SimpleStageTour.displayName = "SimpleStageTour";

export default SimpleStageTour;
